/**
 * Same responsibilities: hamburger navigation, platform switch, store
 * multi-select persisted in localStorage, language selector, logout.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { PLATFORM, ROLES, type Platform } from '@/shared/constants';
import {
  appConfigQueryOptions,
  getSelectedStoreIds,
  setSelectedStoreIds,
  type Store,
  type StoreId,
} from '@/shared/config/app-config';
import { getUserProfile, logout } from '@/features/auth/session';
import { hardNavigate } from '@/shared/lib/navigation';
import { changeLanguage } from '@/shared/i18n/i18n';
import { CheckboxTree } from '@/shared/ui/checkbox-tree/CheckboxTree';
import { NavigationMenu } from './NavigationMenu';
import logo from '@/assets/images/logo_b.png';
import {
  authorizedNavigationItems,
  initialStoreSelection,
  pickLanguage,
  platformRedirect,
  selectionToPersist,
  sortByName,
  storeDropdownTitle,
  switchPlatformTarget,
} from './top-menu.logic';

export interface TopMenuProps {
  platform?: Platform;
  /** Fired with the initial selection, then (debounced) whenever the selection changes. */
  onStoreSelected?: (storeIds: StoreId[]) => void;
}

const STORE_BREAKDOWN = ['region', 'country'] as const;
const EMIT_DEBOUNCE_MS = 500;

/** A store selection and the store list it was made on. */
interface Selection {
  source: readonly Store[];
  ids: StoreId[];
}

export function TopMenu({
  platform = PLATFORM.ABCDIOR,
  onStoreSelected,
}: Readonly<TopMenuProps>) {
  const { i18n } = useTranslation();
  const location = useLocation();
  const user = useMemo(() => getUserProfile(), []);
  const { data: config } = useQuery(appConfigQueryOptions());

  const stores = useMemo(
    () => sortByName(config?.stores ?? []),
    [config?.stores],
  );
  const languages = useMemo(
    () => [...(config?.languages ?? [])].sort((a, b) => a.localeCompare(b)),
    [config?.languages],
  );

  // Selections are tied to the store list they were made on: a refresh of the
  // config (a store created from the stores screen) recomputes the selection
  // from the persisted ids on every refresh.
  const [configSelection, setConfigSelection] = useState<Selection | null>(
    null,
  );
  const [userSelection, setUserSelection] = useState<Selection | null>(null);
  if (user && config && configSelection?.source !== stores) {
    setConfigSelection({
      source: stores,
      ids: initialStoreSelection(stores, getSelectedStoreIds(), user),
    });
  }
  const selectedStores =
    userSelection?.source === stores
      ? userSelection.ids
      : (configSelection?.ids ?? []);
  const [chosenLanguage, setChosenLanguage] = useState<string | null>(null);
  const language = chosenLanguage ?? pickLanguage(languages, user?.language);
  const [menuOpen, setMenuOpen] = useState(false);
  const [storesOpen, setStoresOpen] = useState(false);
  const emitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const storesRef = useRef<HTMLDivElement>(null);

  const canAccessConfigInterfaces =
    user?.features?.CONFIG_INTERFACES_ACCESS ?? true;
  const role = user?.role ?? '';
  const isWorkshop = role === ROLES.WORKSHOP;

  // A user opening a page of a platform they cannot access is sent to their home.
  useEffect(() => {
    if (!user) return;
    const redirect = platformRedirect(user, location.pathname);
    if (redirect) hardNavigate(redirect);
  }, [user, location.pathname]);

  const onStoreSelectedRef = useRef(onStoreSelected);
  useEffect(() => {
    onStoreSelectedRef.current = onStoreSelected;
  });

  // The selection computed from the config is persisted and reported to the
  // page right away.
  useEffect(() => {
    if (!configSelection) return;
    const persisted = selectionToPersist(
      configSelection.source,
      configSelection.ids,
    );
    setSelectedStoreIds(persisted);
    onStoreSelectedRef.current?.(persisted);
  }, [configSelection]);

  // Language: the user's, else the first available. i18next is the external system kept in sync.
  useEffect(() => {
    if (language && i18n.language !== language)
      void changeLanguage(language, i18n);
  }, [language, i18n]);

  // Close popovers on outside click.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current && !menuRef.current.contains(target))
        setMenuOpen(false);
      if (storesRef.current && !storesRef.current.contains(target))
        setStoresOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(
    () => () => {
      if (emitTimer.current) clearTimeout(emitTimer.current);
    },
    [],
  );

  const applySelection = (ids: StoreId[]) => {
    setUserSelection({ source: stores, ids });
    const persisted = selectionToPersist(stores, ids);
    setSelectedStoreIds(persisted);
    if (!configSelection || !onStoreSelected) return;
    if (emitTimer.current) clearTimeout(emitTimer.current);
    emitTimer.current = setTimeout(
      () => onStoreSelected(persisted),
      EMIT_DEBOUNCE_MS,
    );
  };

  const onLanguageChange = (next: string) => {
    setChosenLanguage(next);
    void changeLanguage(next, i18n);
  };

  const switchTo = (target: Platform) =>
    hardNavigate(switchPlatformTarget(platform, location.pathname, target));

  if (!user) return null;

  const showSwitch =
    (user.abcdiorAccess ? 1 : 0) + (user.dmsAccess ? 1 : 0) > 1;

  return (
    <header className="topmenu py-4 leading-none">
      <div className="flex min-h-14 flex-nowrap items-center justify-between gap-4">
        <div className="w-2/12 pt-1" ref={menuRef}>
          <div className="relative">
            <button
              type="button"
              aria-label="menu"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
              className="flex h-10 w-10 items-center justify-center text-2xl hover:bg-neutral-100"
              data-testid="header-menu-button"
            >
              <span aria-hidden="true">☰</span>
            </button>
            {menuOpen && (
              <NavigationMenu
                items={authorizedNavigationItems(
                  platform,
                  role,
                  canAccessConfigInterfaces,
                )}
                onLogout={() => logout()}
              />
            )}
          </div>
        </div>

        {showSwitch && (
          <div className="flex w-3/12 flex-col pt-1">
            {user.abcdiorAccess && platform !== PLATFORM.ABCDIOR && (
              <button
                type="button"
                onClick={() => switchTo(PLATFORM.ABCDIOR)}
                className="cp mt-2 text-left"
                data-testid="header-switch-abcdior-button"
              >
                Switch to ABC Dior
              </button>
            )}
            {user.dmsAccess && platform !== PLATFORM.DMS && (
              <button
                type="button"
                onClick={() => switchTo(PLATFORM.DMS)}
                className="cp mt-2 text-left"
                data-testid="header-switch-mtm-button"
              >
                Switch to MTM
              </button>
            )}
          </div>
        )}

        <div className="w-2/12 text-center">
          <img src={logo} alt="Dior" className="logo mx-auto h-10" />
        </div>

        <div className="w-2/12 pt-1 text-right">
          {!isWorkshop && (
            <>
              <button
                type="button"
                onClick={() => applySelection([])}
                className="cp"
                data-testid="store-filter-clear-button"
              >
                Clear
              </button>
              &nbsp;
              <button
                type="button"
                onClick={() => applySelection(stores.map((store) => store.id))}
                className="cp"
                data-testid="store-filter-select-all-button"
              >
                Select all
              </button>
            </>
          )}
        </div>

        <div className="flex w-3/12 items-center justify-end">
          {!isWorkshop && (
            <div className="relative mr-4" ref={storesRef}>
              <button
                type="button"
                aria-haspopup="true"
                aria-expanded={storesOpen}
                onClick={() => setStoresOpen((open) => !open)}
                className="nav-store-dropdown text-center"
                data-testid="store-filter-button"
              >
                {storeDropdownTitle(stores, selectedStores)}{' '}
                <span aria-hidden="true">▾</span>
              </button>
              {storesOpen && (
                <div
                  className="dropdown-menu absolute left-0 top-full z-20 max-h-[70vh] overflow-auto text-left"
                  role="region"
                  aria-label="Stores"
                >
                  <CheckboxTree
                    rows={stores}
                    breakdown={STORE_BREAKDOWN}
                    idKey="id"
                    labelKey="name"
                    selectedIds={selectedStores}
                    onChange={applySelection}
                    testId="store-filter-tree"
                  />
                </div>
              )}
            </div>
          )}
          <label className="sr-only" htmlFor="topmenu-language">
            Language
          </label>
          <select
            id="topmenu-language"
            value={language ?? ''}
            onChange={(event) => onLanguageChange(event.target.value)}
            className="w-[50px] border-b border-neutral-500 bg-transparent py-1 text-sm uppercase"
            data-testid="header-language-select"
          >
            {languages.map((item) => (
              <option key={item} value={item}>
                {item.toUpperCase()}
              </option>
            ))}
          </select>
        </div>
      </div>
    </header>
  );
}
